// src/components/resources/layouts/AnalyzerLayout.tsx

'use client'

import { logClientError } from '~/lib/logger-client'
import type { ReactNode } from 'react'
import type { ResourceTypeMap } from '~/lib/resources/types/shared/base'
import { ResourceType } from '~/lib/resources/types/shared/enums'
import { motion } from 'motion/react'
import Image from 'next/image'
import RiCalculatorLine from '~/icons/remix/generated/RiCalculatorLine'
import { useEffect, useState } from 'react'
import { Breadcrumbs } from '~/components/resources/Breadcrumbs'
import { RelatedResourcesSection } from '~/components/resources/RelatedResourcesSection'
import useScroll from '~/lib/use-scroll'
import type { AnalyzerRelatedResources } from '~/lib/resources/api/related-resources'
import { FAQ } from '~/components/shared/FAQ'
import { renderInlineMarkdown } from '~/lib/markdown/renderInlineMarkdown'

type Calculator = Extract<ResourceTypeMap['tool'], { toolType: 'calculator' }>
type Analyzer = Extract<ResourceTypeMap['tool'], { toolType: 'analyzer' }>

interface AnalyzerLayoutProps {
  resource: Analyzer
  children: React.ReactNode
  relatedResources?: AnalyzerRelatedResources
}

interface RelatedResourcesResponse {
  related: (ResourceTypeMap['tool'] | ResourceTypeMap['glossary-term'])[]
  metadata: {
    resultsCount: number
  }
}

function RelatedContentView({
  resources,
}: {
  resources: AnalyzerRelatedResources
}): ReactNode {
  return (
    <RelatedResourcesSection
      groups={[
        { title: 'Related Calculators', resources: resources.calculators },
        { title: 'Related Analyzers', resources: resources.analyzers },
        { title: 'Related Terms', resources: resources.glossaryTerms },
      ]}
    />
  )
}

function RelatedContentWithFetch({
  currentSlug,
  resource,
}: {
  currentSlug: string
  resource: Analyzer
}): ReactNode {
  const [relatedResources, setRelatedResources] = useState<AnalyzerRelatedResources>({
    calculators: [],
    analyzers: [],
    glossaryTerms: [],
  })
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false)

  useEffect(() => {
    let isMounted = true

    async function fetchRelated(): Promise<void> {
      try {
        const params = new URLSearchParams({
          type: ResourceType.TOOL,
          slug: currentSlug,
          maxResults: '9',
          // Match server-side `topRankedByScore` default (minScore 1) for parity
          // when the SSR-precomputed bundle is not provided.
          minScore: '1',
          requireTypes: `${ResourceType.TOOL},${ResourceType.GLOSSARY_TERM}`,
          relationshipType: 'all',
          prioritizeExplicit: 'true',
        })

        const response = await fetch(`/api/resources/related?${params.toString()}`, {
          cache: 'no-store',
        })

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(`Failed to fetch related resources: ${errorText}`)
        }

        const data = await response.json() as RelatedResourcesResponse

        const calculators = data.related
          .filter((resourceItem): resourceItem is Calculator =>
            resourceItem.resourceType === ResourceType.TOOL &&
            'toolType' in resourceItem &&
            resourceItem.toolType === 'calculator',
          )
          .slice(0, 3)

        const analyzers = data.related
          .filter((resourceItem): resourceItem is Analyzer =>
            resourceItem.resourceType === ResourceType.TOOL &&
            'toolType' in resourceItem &&
            resourceItem.toolType === 'analyzer',
          )
          .slice(0, 3)

        const glossaryTerms = data.related
          .filter((resourceItem): resourceItem is ResourceTypeMap['glossary-term'] =>
            resourceItem.resourceType === ResourceType.GLOSSARY_TERM,
          )
          .slice(0, 3)

        if (isMounted) {
          setRelatedResources({ calculators, analyzers, glossaryTerms })
          setHasAttemptedFetch(true)
        }
      } catch (error: unknown) {
        if (isMounted) {
          logClientError(error, { component: 'AnalyzerLayout', context: 'Error fetching related resources' })
          setRelatedResources({ calculators: [], analyzers: [], glossaryTerms: [] })
          setHasAttemptedFetch(true)
        }
      }
    }

    void fetchRelated()

    return () => {
      isMounted = false
    }
  }, [currentSlug, resource])

  if (!hasAttemptedFetch) return null
  return <RelatedContentView resources={relatedResources} />
}

function RelatedContent({
  resource,
  precomputed,
}: {
  resource: Analyzer
  precomputed?: AnalyzerRelatedResources
}): ReactNode {
  if (precomputed) return <RelatedContentView resources={precomputed} />
  return <RelatedContentWithFetch currentSlug={resource.slug} resource={resource} />
}

function AnalyzerIntro({ intro }: { intro?: string }): JSX.Element | null {
  if (!intro?.trim()) return null
  return (
    <div className="mt-10 max-w-3xl">
      <p className="text-base leading-relaxed text-gray-600 dark:text-gray-400">
        {renderInlineMarkdown(intro)}
      </p>
    </div>
  )
}

function AnalyzerFaq({ resource }: { resource: Analyzer }): JSX.Element | null {
  if (!resource.faqs || resource.faqs.length === 0) return null
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="mt-16"
    >
      <h2 className="mb-4 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
        Frequently Asked Questions
      </h2>
      <FAQ
        items={resource.faqs.map((item) => ({
          ...item,
          // Mirror GeneratorLayout's FaqAnswer: render inline markdown
          // (`[label](href)`, `**bold**`) in string answers so links/emphasis
          // don't surface as literal tokens. JSON-LD strips it via
          // `inlineMarkdownToText` (metadata.ts), keeping page + schema in sync.
          answer:
            typeof item.answer === 'string'
              ? renderInlineMarkdown(item.answer)
              : item.answer,
        }))}
        type="single"
        title=""
        disableSchema={true}
      />
    </motion.div>
  )
}

export function AnalyzerLayout({
  resource,
  children,
  relatedResources,
}: AnalyzerLayoutProps): JSX.Element {
  const scrolled = useScroll(50)

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <div className="fixed top-0 left-0 right-0 z-40">
        {/* Once scrolled, the backdrop must be fully OPAQUE with a hard bottom
            edge: the previous translucent gradient tail let page content
            (table headers, section titles) ghost through the breadcrumb row
            as a text-on-text collision on long analyzer pages. The gradient
            treatment is kept only at scroll-top, where nothing has scrolled
            under the header yet. */}
        <div className={`absolute inset-0 transition-colors duration-200 ${
          scrolled
            ? 'border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950'
            : 'bg-gradient-to-b from-white via-white/95 to-white/80 dark:from-gray-950 dark:via-gray-950/95 dark:to-gray-950/80'
        }`} />

        <div className="h-20" />

        {/* The canonical title is already here via the `resource` prop, so the
            crumb label ships in the first-pass SSR HTML instead of arriving
            via a client fetch. */}
        <div className="relative pb-1">
          <Breadcrumbs leafLabel={resource.canonicalTitle ?? resource.title} />
        </div>
      </div>

      <div className="pt-[148px]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-12 items-start">
            {/*
              The heading/description sidebar is FIRST in the DOM so the reading
              order (keyboard + screen reader) leads with the page <h1> and
              context — not the tool, intro, and full FAQ. CSS `order` alone
              would only reposition visually and leave assistive tech hitting the
              extras first, so this is real source order, not an order hack. On
              desktop `lg:order-last` floats it back to
              the right-hand sticky column; the tool block takes `lg:order-first`
              + `lg:col-span-2` to fill the left two tracks. On mobile (single
              column) source order applies directly: heading, then tool/intro/FAQ.
            */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:order-last lg:sticky lg:top-[148px]"
            >
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl mb-6">
                {resource.title}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {resource.description}
              </p>

              <div className="mt-12 space-y-8">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-500/10">
                    <RiCalculatorLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Free Analyzer
                    </h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      No sign up required. Use this analyzer as much as you need.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              // min-w-0 lets this grid item shrink below its content's
              // max-content width, so wide analyzer content (e.g. the
              // paid-social cited tables) scrolls inside its own
              // overflow-x-auto wrapper instead of widening the whole page
              // past the viewport on mobile (grid items default to
              // min-width:auto, which propagates a table's min-width up).
              className="min-w-0 lg:order-first lg:col-span-2"
            >
              <div className="rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 dark:bg-gray-800/50 dark:ring-gray-800">
                <div className="relative h-48 w-full overflow-hidden rounded-lg">
                  <Image
                    src={resource.images?.hero?.url ?? resource.images?.og?.url ?? '/images/resources/tools/adsights-digital-marketing-resource-center-interactive-tools-marketing-calculators-quizzes-analyzers-hero-image.webp'}
                    alt={resource.title}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 66vw, 800px"
                    fetchPriority="high"
                    className="object-cover"
                  />
                </div>
                {children}
              </div>
              <AnalyzerIntro intro={resource.intro} />
              <AnalyzerFaq resource={resource} />
              {/*
                Related links live AFTER the tool + intro + FAQ in source order so
                mobile / screen-reader users reach the analyzer (and its FAQ)
                before the related-resource rail, rather than encountering related
                links before the tool when the grid collapses to one column. On
                desktop this sits at the foot of the
                main column under the FAQ.
              */}
              <RelatedContent resource={resource} precomputed={relatedResources} />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}
